/**
 * Public Portfolio API Service (Phase 4).
 *
 * Fetches `GET /api/v1/realtors/public/:slug` — the unauthenticated
 * public realtor profile + active listings + sold count. Same
 * existence-leak posture as the public listing endpoint: 404 for
 * unpublished/unverified slugs, no differentiation from "doesn't exist".
 *
 * The wire shape intentionally mirrors the backend `PortfolioPayload`
 * but with ISO date strings.
 */

import { apiClient } from "@/lib/api-client";

const API_BASE = "/api/v1";

export type PropertyType = "apartment" | "house" | "villa" | "plot";
export type TransactionType = "sale" | "rental" | "commercial";
export type ListingStatus =
	| "active"
	| "under_negotiation"
	| "sold"
	| "withdrawn";

export interface PublicRealtor {
	id: string;
	name: string;
	agencyName: string | null;
	bio: string | null;
	serviceCities: string[];
	serviceLocalities: string[];
	specializations: string[];
	profileImageUrl: string | null;
	yearsExperience: number | null;
	languages: string[];
	reraNumber: string | null;
	reraVerified: boolean;
	idVerified: boolean;
	publicSlug: string;
}

export interface PublicPortfolioListing {
	id: string;
	publicSlug: string | null;
	propertyType: PropertyType;
	transactionType: TransactionType;
	bhk: number | null;
	sizeSqft: number | null;
	addressPublic: string;
	city: string;
	locality: string;
	askingPrice: number;
	headline: string;
	description: string | null;
	images: string[];
	amenities: string[];
	status: ListingStatus;
}

export interface PortfolioPayload {
	realtor: PublicRealtor;
	activeListings: PublicPortfolioListing[];
	soldCount: number;
}

interface BackendErrorBody {
	message?: string;
	error?: string;
}
interface AxiosLikeError {
	response?: { data?: BackendErrorBody; status?: number };
	code?: string;
	message?: string;
}

export type PortfolioFetchResult =
	| { success: true; portfolio: PortfolioPayload }
	| { success: false; status: "not-found" }
	| { success: false; status: "error"; message: string };

export async function getPublicPortfolio(
	slug: string,
): Promise<PortfolioFetchResult> {
	try {
		const response = await apiClient.get<{ data: PortfolioPayload }>(
			`${API_BASE}/realtors/public/${encodeURIComponent(slug)}`,
		);
		return { success: true, portfolio: response.data.data };
	} catch (error: unknown) {
		const err = error as AxiosLikeError;
		if (err.response?.status === 404) {
			return { success: false, status: "not-found" };
		}
		if (!err.response) {
			return {
				success: false,
				status: "error",
				message:
					err.code === "ECONNABORTED" || err.message?.includes("timeout")
						? "Request timed out. Check your connection and try again."
						: "Couldn't reach the server. Check your connection and try again.",
			};
		}
		return {
			success: false,
			status: "error",
			message: err.response.data?.message ?? "Couldn't load this portfolio.",
		};
	}
}

// Mirrors the envelope the rest of the realtor / listings services
// return: best-effort `errorCode` from the backend's structured `error`
// string + a human-readable `error` from `message`. See
// `realtor-api.service.ts` for the seed pattern.
interface ApiResult<T> {
	success: boolean;
	error?: string;
	errorCode?: string | null;
	data?: T;
}

function buildPublishFailure<T>(
	error: unknown,
	fallback: string,
): ApiResult<T> {
	if (error == null) {
		return { success: false, error: fallback, errorCode: "UNKNOWN_ERROR" };
	}
	const err = error as AxiosLikeError;
	if (!err.response) {
		return {
			success: false,
			error:
				err.code === "ECONNABORTED" || err.message?.includes("timeout")
					? "Request timed out. Check your connection and try again."
					: "Couldn't reach the server. Check your connection and try again.",
			errorCode: "NETWORK_ERROR",
		};
	}
	return {
		success: false,
		error: err.response.data?.message ?? fallback,
		errorCode: err.response.data?.error ?? null,
	};
}

interface PublishResponse {
	profile: { publicSlug: string | null; portfolioPublished: boolean };
}

/**
 * Authenticated — flips portfolio_published=true. Backend allocates a
 * slug on first publish and returns the updated profile row.
 */
export async function publishMyPortfolio(): Promise<
	ApiResult<PublishResponse>
> {
	try {
		const response = await apiClient.post<{ data: PublishResponse }>(
			`${API_BASE}/realtors/me/portfolio/publish`,
		);
		return { success: true, data: response.data.data };
	} catch (error: unknown) {
		return buildPublishFailure<PublishResponse>(
			error,
			"Couldn't publish your portfolio. Make sure you're verified first.",
		);
	}
}

/**
 * Authenticated — flips portfolio_published=false. The slug is
 * preserved on the backend so re-publishing returns the same URL.
 */
export async function unpublishMyPortfolio(): Promise<
	ApiResult<PublishResponse>
> {
	try {
		const response = await apiClient.post<{ data: PublishResponse }>(
			`${API_BASE}/realtors/me/portfolio/unpublish`,
		);
		return { success: true, data: response.data.data };
	} catch (error: unknown) {
		return buildPublishFailure<PublishResponse>(
			error,
			"Couldn't unpublish your portfolio.",
		);
	}
}
